import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import ContactStudentModal from '../components/ContactStudentModal';
import {
  UserIcon,
  AcademicCapIcon,
  TrophyIcon,
  StarIcon,
  MapPinIcon,
  BriefcaseIcon,
  CalendarIcon,
  EnvelopeIcon,
  ArrowLeftIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface StudentProfile {
  id: string;
  displayName: string;
  avatar?: string;
  bio?: string;
  location?: string;
  skills?: string[];
  education?: {
    degree: string;
    institution: string;
    graduationYear: number;
    gpa?: number;
    fieldOfStudy?: string;
  }[];
  experience?: {
    title: string;
    company: string;
    duration: string;
    description: string;
    startDate?: string;
    endDate?: string;
  }[];
  certifications?: {
    name: string;
    issuer: string;
    dateEarned: string;
    expirationDate?: string;
  }[];
  projects?: {
    title: string;
    description: string;
    technologies: string[];
    url?: string;
    githubUrl?: string;
  }[];
  courseStats?: {
    enrolled: number;
    completed: number;
    averageScore: number;
    totalHours: number;
  };
  skillRatings?: {
    skill: string;
    rating: number;
    verified: boolean;
  }[];
  overallRanking?: number;
}

const StudentProfileViewPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<StudentProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userId) {
      fetchStudentProfile();
      trackProfileView();
    }
  }, [userId]);

  const fetchStudentProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/profiles/student/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Student profile not found');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setProfile(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch student profile');
      }
    } catch (error: any) {
      console.error('Error fetching student profile:', error);
      setError(error.message);
      toast.error('Failed to load student profile');
    } finally {
      setLoading(false);
    }
  };

  const trackProfileView = async () => {
    try {
      console.log('Tracking profile view for userId:', userId);
      const token = localStorage.getItem('authToken');
      console.log('Auth token exists:', !!token);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/profile-views/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          profileOwnerId: userId,
          source: 'direct_link',
          pagesViewed: ['profile_overview']
        })
      });
      
      console.log('Profile view response:', response.status, response.statusText);
      const result = await response.json();
      console.log('Profile view result:', result);
    } catch (error) {
      console.error('Error tracking profile view:', error);
    }
  };

  const handleContact = () => {
    if (profile) {
      setShowContactModal(true);
    }
  };

  // Create a compatible student object for the ContactStudentModal
  const studentForModal = profile ? {
    id: profile.id,
    firstName: profile.displayName?.split(' ')[0] || 'Student',
    lastName: profile.displayName?.split(' ').slice(1).join(' ') || '',
    displayName: profile.displayName,
    avatar: profile.avatar,
    skills: profile.skills
  } : null;

  const handleMessageSent = () => {
    toast.success('Message sent successfully!');
    setShowContactModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Profile Not Found</h3>
          <p className="text-gray-600 mb-4">{error || 'The student profile you\'re looking for doesn\'t exist.'}</p>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back
          </button>
        </div>

        {/* Profile Header */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
              <div className="flex items-center mb-4 sm:mb-0">
                <div className="h-20 w-20 bg-indigo-100 rounded-full flex items-center justify-center mr-4">
                  {profile.avatar ? (
                    <img src={profile.avatar} alt="" className="h-20 w-20 rounded-full object-cover" />
                  ) : (
                    <UserIcon className="h-10 w-10 text-indigo-600" />
                  )}
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{profile.displayName}</h1>
                  {profile.location && (
                    <div className="flex items-center mt-1">
                      <MapPinIcon className="h-4 w-4 text-gray-400 mr-1" />
                      <span className="text-gray-600">{profile.location}</span>
                    </div>
                  )}
                  {profile.overallRanking && (
                    <div className="flex items-center mt-1">
                      <StarIcon className="h-4 w-4 text-purple-500 mr-1" />
                      <span className="text-sm text-gray-600">Ranking: #{profile.overallRanking}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <button
                onClick={handleContact}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <EnvelopeIcon className="h-4 w-4 mr-2" />
                Contact Candidate
              </button>
            </div>

            {/* Bio */}
            {profile.bio && (
              <div className="mt-4">
                <p className="text-gray-600">{profile.bio}</p>
              </div>
            )}

            {/* Course Stats */}
            {profile.courseStats && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{profile.courseStats.completed}</div>
                  <div className="text-sm text-gray-500">Courses Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{profile.courseStats.averageScore}%</div>
                  <div className="text-sm text-gray-500">Average Score</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{profile.courseStats.totalHours}</div>
                  <div className="text-sm text-gray-500">Hours Studied</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-600">{profile.courseStats.enrolled}</div>
                  <div className="text-sm text-gray-500">Currently Enrolled</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Skills */}
            {profile.skills && profile.skills.length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-indigo-100 text-indigo-800 text-sm rounded-full"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Experience */}
            {profile.experience && profile.experience.length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <BriefcaseIcon className="h-5 w-5 mr-2" />
                    Experience
                  </h3>
                  <div className="space-y-4">
                    {profile.experience.map((exp, index) => (
                      <div key={index} className="border-b border-gray-200 pb-4 last:border-b-0">
                        <h4 className="font-semibold text-gray-900">{exp.title}</h4>
                        <p className="text-indigo-600">{exp.company}</p>
                        <p className="text-sm text-gray-500 flex items-center mt-1">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          {exp.duration}
                        </p>
                        {exp.description && (
                          <p className="text-gray-600 mt-2">{exp.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Projects */}
            {profile.projects && profile.projects.length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Projects</h3>
                  <div className="space-y-4">
                    {profile.projects.map((project, index) => (
                      <div key={index} className="border-b border-gray-200 pb-4 last:border-b-0">
                        <h4 className="font-semibold text-gray-900">{project.title}</h4>
                        <p className="text-gray-600 mt-1">{project.description}</p>
                        {project.technologies && project.technologies.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {project.technologies.map((tech, techIndex) => (
                              <span
                                key={techIndex}
                                className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                              >
                                {tech}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex space-x-4 mt-2">
                          {project.url && (
                            <a
                              href={project.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-600 hover:text-indigo-800 text-sm"
                            >
                              View Project
                            </a>
                          )}
                          {project.githubUrl && (
                            <a
                              href={project.githubUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-600 hover:text-indigo-800 text-sm"
                            >
                              View Code
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Education */}
            {profile.education && profile.education.length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <AcademicCapIcon className="h-5 w-5 mr-2" />
                    Education
                  </h3>
                  <div className="space-y-4">
                    {profile.education.map((edu, index) => (
                      <div key={index} className="border-b border-gray-200 pb-4 last:border-b-0">
                        <h4 className="font-semibold text-gray-900">{edu.degree}</h4>
                        <p className="text-indigo-600">{edu.institution}</p>
                        <p className="text-sm text-gray-500">{edu.graduationYear}</p>
                        {edu.fieldOfStudy && (
                          <p className="text-sm text-gray-600 mt-1">{edu.fieldOfStudy}</p>
                        )}
                        {edu.gpa && (
                          <p className="text-sm text-gray-600 mt-1">GPA: {edu.gpa}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Certifications */}
            {profile.certifications && profile.certifications.length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <TrophyIcon className="h-5 w-5 mr-2" />
                    Certifications
                  </h3>
                  <div className="space-y-3">
                    {profile.certifications.map((cert, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium text-gray-900">{cert.name}</h4>
                          <p className="text-sm text-gray-600">{cert.issuer}</p>
                          <p className="text-xs text-gray-500">{cert.dateEarned}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Skill Ratings */}
            {profile.skillRatings && profile.skillRatings.length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Skill Ratings</h3>
                  <div className="space-y-3">
                    {profile.skillRatings.map((skillRating, index) => (
                      <div key={index}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-medium text-gray-900">
                            {skillRating.skill}
                            {skillRating.verified && (
                              <CheckCircleIcon className="inline h-4 w-4 text-green-500 ml-1" />
                            )}
                          </span>
                          <span className="text-sm text-gray-500">{skillRating.rating}/5</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-indigo-600 h-2 rounded-full"
                            style={{ width: `${(skillRating.rating / 5) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contact Modal */}
        {studentForModal && (
          <ContactStudentModal
            isOpen={showContactModal}
            onClose={() => setShowContactModal(false)}
            student={studentForModal}
            onMessageSent={handleMessageSent}
          />
        )}
      </div>
    </div>
  );
};

export default StudentProfileViewPage;